import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Button, Spinner } from "@/components/ui";
import { Download, FileText } from "lucide-react";

// Reusable admin document page: renders a /docs/*.md file with PDF/Markdown downloads and optional stat cards.
export default function MarkdownDocPage({ title, subtitle, mdUrl, pdfKey, pdfName, stats = [], testid = "doc-content" }) {
  const [md, setMd] = useState(null);
  useEffect(() => {
    fetch(mdUrl).then((r) => r.text()).then(setMd).catch(() => setMd("Failed to load document."));
  }, [mdUrl]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/docs/${pdfKey}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = pdfName; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF export failed"); }
  };

  return (
    <div className="vc-fade-up max-w-5xl">
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={downloadPdf} data-testid={`${pdfKey}-pdf`}><FileText className="h-4 w-4" /> PDF</Button>
          <a href={mdUrl} download><Button data-testid={`${pdfKey}-md`}><Download className="h-4 w-4" /> Markdown</Button></a>
        </div>}
      />

      {stats.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map((s) => (
            <Card key={s.label} className="p-5" data-testid="doc-stat">
              <div className="h-10 w-10 rounded-md bg-brand-50 flex items-center justify-center mb-3">
                <s.icon className="h-5 w-5 text-brand-700" strokeWidth={1.75} />
              </div>
              <p className="text-2xl font-bold font-display text-brand-800">{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </Card>
          ))}
        </div>
      )}

      <Card className="p-6 sm:p-10">
        {md === null ? <Spinner label="Loading…" /> : (
          <div className="rfp-prose" data-testid={testid}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{md}</ReactMarkdown>
          </div>
        )}
      </Card>
    </div>
  );
}
